import { isAxiosError } from "axios";
import type { Portal } from "../portal";
import { unwrapItem } from "../apiNormalize";
import { get } from "./helpers";
import { fetchPendingSessionsCount } from "./central";
import {
  fetchAdminSubscriptionRequestsPendingCount,
  fetchAdminPendingFieldChangesPendingCount,
} from "./admin";

/**
 * Consolidated sidebar nav-badge counters (§B13). The backend returns a single
 * lightweight, caller-scoped, integer-only payload (`GET <portal>/nav/counters`,
 * cached 30–60s) so the sidebar no longer has to fan out one count fetch per
 * list. All fields are optional and default to 0 — a portal only populates the
 * counters relevant to it (central vs admin), and the type stays additive so new
 * counters from the backend don't require a frontend change to render.
 */
export interface NavCounters {
  doctorsPending: number;
  sessionsPending: number;
  contractsExpiring: number;
  exportsReady: number;
  notificationsUnread: number;
  subscriptionRequestsPending: number;
  pendingFieldChanges: number;
}

/** Zeroed counters — the stable default before the fetch resolves. */
export const EMPTY_NAV_COUNTERS: NavCounters = {
  doctorsPending: 0,
  sessionsPending: 0,
  contractsExpiring: 0,
  exportsReady: 0,
  notificationsUnread: 0,
  subscriptionRequestsPending: 0,
  pendingFieldChanges: 0,
};

/** Coerce an unknown value to a non-negative integer (backend returns ints). */
function int(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
}

/** Map the snake_case API envelope into the camelCase NavCounters shape. */
function normalizeNavCounters(raw: Record<string, unknown>): NavCounters {
  return {
    doctorsPending: int(raw.doctors_pending),
    sessionsPending: int(raw.sessions_pending),
    contractsExpiring: int(raw.contracts_expiring),
    exportsReady: int(raw.exports_ready),
    notificationsUnread: int(raw.notifications_unread),
    subscriptionRequestsPending: int(raw.subscription_requests_pending),
    pendingFieldChanges: int(raw.pending_field_changes),
  };
}

/**
 * Fallback used until §B13 ships: reassemble the counters object from the
 * existing per-list count fetchers so the badges keep working against the
 * current backend. Each fetcher is tolerant of its own failure (a count that
 * can't be read just stays 0) so one unavailable endpoint doesn't blank the
 * whole sidebar.
 */
async function fetchNavCountersFallback(portal: Portal): Promise<NavCounters> {
  const safe = async (fn: () => Promise<number>): Promise<number> => {
    try {
      return await fn();
    } catch {
      return 0;
    }
  };

  if (portal === "admin") {
    const [subscriptionRequestsPending, pendingFieldChanges] = await Promise.all([
      safe(fetchAdminSubscriptionRequestsPendingCount),
      safe(fetchAdminPendingFieldChangesPendingCount),
    ]);
    return { ...EMPTY_NAV_COUNTERS, subscriptionRequestsPending, pendingFieldChanges };
  }

  const sessionsPending = await safe(fetchPendingSessionsCount);
  return { ...EMPTY_NAV_COUNTERS, sessionsPending };
}

/**
 * Portal-aware fetch of the consolidated sidebar nav counters. Hits the §B13
 * endpoint when present; on a 404 (endpoint not yet deployed) or a network
 * error it transparently falls back to the per-list count fetchers so badge
 * behaviour is preserved. Other errors (auth, server) bubble up to useAsync,
 * which keeps prior cached data and avoids console spam.
 */
export async function fetchNavCounters(portal: Portal): Promise<NavCounters> {
  try {
    const body = await get<unknown>(`/${portal}/nav/counters`);
    const item = unwrapItem<Record<string, unknown>>(body);
    return normalizeNavCounters(item ?? {});
  } catch (err: unknown) {
    // §B13 not shipped yet (404) or transient network failure → reconstruct
    // the counters from the legacy per-list fetchers. Remove this fallback once
    // the consolidated endpoint is live on all environments.
    const isNotFound = isAxiosError(err) && err.response?.status === 404;
    const isNetwork = isAxiosError(err) && err.response === undefined;
    if (isNotFound || isNetwork) {
      return fetchNavCountersFallback(portal);
    }
    throw err;
  }
}
